import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import {
  Grid,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";

const NavLink = (props) => {
  const { title, mobileTitle, href, icon, active, addLive, showIcons } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [hideIndicator, setHideIndicator] = useState(true);

  useEffect(() => {
    if (!active) {
      setHideIndicator(true);
    } else setHideIndicator(false);
  }, [active]);

  // Mobile: a compact, equal-width tab — icon stacked over a short label — so
  // all six links fit on one row with no horizontal scrolling. The active tab
  // is shown in the accent color (no sliding indicator needed). A short
  // mobileTitle (e.g. "Ranks" for "Leaderboard") keeps long labels in their cell.
  if (isMobile) {
    const label = mobileTitle || title;
    return (
      <Grid
        container
        sx={{
          flex: 1,
          minWidth: 0,
          justifyContent: "center",
          padding: "6px 2px",
          zIndex: 2,
        }}
      >
        <Link
          to={href}
          style={{
            textDecoration: "none",
            cursor: active ? "default" : "pointer",
            width: "100%",
          }}
        >
          <Grid
            container
            direction="column"
            wrap="nowrap"
            sx={[
              {
                alignItems: "center",
                gap: "2px",
                transition: "color 0.3s ease-out",
                color: active
                  ? theme.branding.cyan[500]
                  : theme.branding.gray[500],
              },
              (theme) =>
                theme.applyStyles("dark", {
                  color: active
                    ? theme.branding.cyan[300]
                    : theme.branding.gray[300],
                }),
            ]}
          >
            {showIcons && (
              <SvgIcon
                component={icon}
                sx={{ color: "inherit", height: "20px", width: "20px" }}
              />
            )}
            <Grid
              container
              direction="row"
              wrap="nowrap"
              sx={{ width: "auto", alignItems: "center", gap: "3px" }}
            >
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: active ? 700 : 500,
                  color: "inherit",
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Typography>
              {addLive && (
                <CircleIcon
                  htmlColor={theme.branding.red[500]}
                  sx={{
                    color: theme.branding.red[500],
                    height: "7px",
                    width: "7px",
                  }}
                />
              )}
            </Grid>
          </Grid>
        </Link>
      </Grid>
    );
  }

  return (
    <Grid
      container
      sx={[
        {
          justifyContent: "center",
          padding: "8px 12px",
          background: hideIndicator ? theme.branding.gray[0] : "none",
          zIndex: 2,
          transition: "all 0.5s ease-out",
          boxShadow: "none",
        },
        (theme) =>
          theme.applyStyles("dark", {
            background: hideIndicator ? theme.branding.darkBlue[800] : "none",
            boxShadow: active
              ? `0px 16px 30px -16px ${theme.branding.cyan[500]}, 0px 16px 30px -16px ${theme.branding.cyan[800]}`
              : "none",
          }),
      ]}
    >
      <Link
        to={href}
        style={{
          textDecoration: "none",
          cursor: active ? "default" : "pointer",
          justifyContent: "center",
          transition: "all 0.5s ease-out",
        }}
      >
        <Grid
          container
          spacing="6px"
          direction="row"
          wrap="nowrap"
          sx={[
            {
              justifyContent: "center",
              alignItems: "center",
              transition: "all 0.5s ease-out",
              transform: active ? "none" : "translateY(-2px)",
              color: active
                ? theme.branding.cyan[200]
                : theme.branding.gray[500],
              "&:hover": {
                color: active
                  ? theme.branding.cyan[200]
                  : theme.branding.gray[700],
              },
            },
            (theme) =>
              theme.applyStyles("dark", {
                color: active
                  ? theme.branding.cyan[200]
                  : theme.branding.gray[300],
                "&:hover": {
                  color: active
                    ? theme.branding.cyan[200]
                    : theme.branding.gray[200],
                },
              }),
          ]}
        >
          {showIcons && (
            <SvgIcon
              component={icon}
              htmlColor={theme.branding.cyan[200]}
              sx={{
                color: "inherit",
                height: "16px",
                width: "16px",
                alignSelf: "center",
                "&:hover": {
                  color: "inherit",
                },
              }}
            />
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              textDecoration: "none",
              color: "inherit",
              lineHeight: "20px",
            }}
          >
            {title}
          </Typography>
          {addLive && (
            <Grid
              container
              direction="row"
              spacing="4px"
              wrap="nowrap"
              sx={{
                backgroundColor: theme.branding.red[500],
                padding: "2px 8px",
                borderRadius: "16px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircleIcon
                htmlColor={theme.branding.gray[0]}
                sx={{
                  color: theme.branding.gray[0],
                  opacity: "60%",
                  height: "6px",
                  width: "6px",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: theme.branding.gray[0],
                  fontSize: "10px",
                  fontWeight: 700,
                  alignSelf: "center",
                }}
              >
                LIVE
              </Typography>
            </Grid>
          )}
        </Grid>
      </Link>
    </Grid>
  );
};

export default NavLink;
